import type { MediaRef, AccessSession } from "@/content/types";

const PUBLIC_BASE = import.meta.env.VITE_R2_PUBLIC_BASE_URL ?? "";

function joinBase(base: string, key: string): string {
  if (!base) return `/${key.replace(/^\/+/, "")}`;
  return `${base.replace(/\/+$/, "")}/${key.replace(/^\/+/, "")}`;
}

/** Resolve a public MediaRef to a URL synchronously. */
export function resolvePublic(key: string): string {
  return joinBase(PUBLIC_BASE, key);
}

/**
 * Resolve any MediaRef to a URL.
 * - public: returns synchronously via VITE_R2_PUBLIC_BASE_URL + key.
 * - private: requires an access session; returns a signed URL via /api/sign-media.
 * - external: passes through.
 */
export async function resolveMedia(
  ref: MediaRef,
  session: AccessSession | null = null,
): Promise<string> {
  switch (ref.kind) {
    case "public":
      return resolvePublic(ref.key);
    case "external":
      return ref.url;
    case "private": {
      if (!session) throw new Error("private_media_requires_session");
      const url = new URL("/api/sign-media", window.location.origin);
      url.searchParams.set("token", session.token);
      url.searchParams.set("key", ref.key);
      url.searchParams.set("kind", "r2");
      const res = await fetch(url.toString());
      if (!res.ok) throw new Error(`sign_media_failed_${res.status}`);
      const body = (await res.json()) as { url: string; expiresInSec: number };
      return body.url;
    }
  }
}
